import { mySupportersQuery } from "@/api/me";
import { Box, Flex, Skeleton, Span, Text } from "@givenwell/components";
import {
  BicycleIcon,
  BrainCognitiveIcon,
  ForkSpoonIcon,
  HeartFilledIcon,
  IconProps,
  MassageIcon,
  ScreenIcon,
  SleepIcon,
  StethoscopeIcon,
} from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FunctionComponent, ReactNode, Suspense } from "react";

export const Route = createFileRoute("/_auth/_app/claims/policy/allowance")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <>
      <Suspense fallback={<Skeleton css={{ height: 200 }} />}>
        <WhatCanIClaim />
      </Suspense>
    </>
  );
}

type Example = {
  title: string;
  icon: FunctionComponent<IconProps>;
  yes?: string[];
  no?: string[];
};

const M4R_SUPPORTER_ID = "supp_cm8hzotof002c0760zjb43ttu";

const EXAMPLES: Example[] = [
  {
    title: "Healthcare",
    icon: StethoscopeIcon,
    yes: ["GP visits", "Dentists", "Optometry", "Prescriptions", "Specialists", "Vaccinations", "Health insurance"],
  },
  {
    title: "Fitness & Training",
    icon: BicycleIcon,
    yes: [
      "Gym memberships",
      "Yoga classes",
      "Pilates",
      "Personal training",
      "Group fitness",
      "Sports equipment",
      "Sports club fees",
    ],
  },
  {
    title: "Mental Health",
    icon: BrainCognitiveIcon,
    yes: ["Counselling", "Psychotherapy", "Psychology", "Life-coaching", "Meditation apps"],
  },
  {
    title: "Nutrition",
    icon: ForkSpoonIcon,
    yes: ["Vitamins & Supplements", "Protein powder", "Diet tracking apps", "Dietitian consultations"],
  },
  {
    title: "Physical Therapy",
    icon: MassageIcon,
    yes: ["Physiotherapy", "Massage Therapy", "Osteopathy"],
  },
  {
    title: "Sleep Health",
    icon: SleepIcon,
    yes: ["Sleep therapy", "Mattresses", "Eye masks", "Child sleep training"],
  },
];

const M4R_EXAMPLES: Example[] = [
  {
    title: "Fitness & Training",
    icon: HeartFilledIcon,
    yes: [
      "Gym memberships",
      "Yoga",
      "Pilates",
      "Dance",
      "Martial arts",
      "Aqua aerobics",
      "Sports and recreation fees (your own)",
    ],
  },
  {
    title: "Fitness Gear",
    icon: BicycleIcon,
    yes: ["Home gym equipment", "Running/hiking shoes", "Bicycles and repairs", "Sports attire"],
  },
  {
    title: "Apps",
    icon: BrainCognitiveIcon,
    yes: ["Fitness and mindfulness apps"],
  },
  {
    title: "Physical Therapy",
    icon: MassageIcon,
    yes: ["Massage", "Mirimiri", "Physiotherapy"],
  },
  {
    title: "Home Office",
    icon: ScreenIcon,
    yes: ["Keyboard, mouse, monitor, desk or chair to support a safe workstation at home"],
  },
];

type FAQ = {
  question: ReactNode;
  answer: ReactNode;
};

function WhatCanIClaim() {
  const { data: supporters } = useSuspenseQuery(mySupportersQuery());
  const isM4R = supporters.supporters.some(s => s.supporter_id === M4R_SUPPORTER_ID);

  const examples = isM4R ? M4R_EXAMPLES : EXAMPLES;

  return (
    <>
      <p>
        {isM4R
          ? "The following purchases are covered under your employer's wellbeing policy:"
          : "We cover any purchase that primarily supports your physical or mental wellbeing. For example: "}
      </p>

      <Box css={{ spaceY: 12 }}>
        {examples.map(example => (
          <Box key={example.title}>
            {example.yes && (
              <Flex css={{ items: "flex-start", gap: 12 }}>
                <example.icon style={{ flexShrink: 0 }} />
                <Text css={{ py: 0 }}>
                  <Span css={{ weight: 600 }}>{example.title}: </Span>
                  {example.yes?.join(", ")}
                </Text>
              </Flex>
            )}
          </Box>
        ))}
      </Box>
      {isM4R ? (
        <p>
          These categories are specific to your employer's policy. If you're unsure whether something qualifies, please
          reach out to us at{" "}
          <a href="mailto:claims@givenwell.com">
            <Span css={{ color: colors.blue600, weight: 500 }}>claims@givenwell.com</Span>
          </a>
          .
        </p>
      ) : (
        <p>
          The examples above are not exhaustive, and coverage may be subject to change. The ultimate decision on what's
          covered is subject to your company's own policies. If you want to make sure something qualifies before you
          purchase it, please reach out to us at{" "}
          <a href="mailto:claims@givenwell.com">
            <Span css={{ color: colors.blue600, weight: 500 }}>claims@givenwell.com</Span>
          </a>
          .
        </p>
      )}
    </>
  );
}
